class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Checkboxes natively toggle with Space only; also support Enter for keyboard users.
    facetForm.addEventListener('keydown', (event) => {
      if (
        event.target.classList.contains('facets__checkbox') &&
        (event.code === 'Enter' || event.key === 'Enter')
      ) {
        event.preventDefault();
        event.target.click();
      }
    });

    this.querySelectorAll('details').forEach((el) => {
      new Accordion(el);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collection) collection.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const source = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;

    target.innerHTML = source.innerHTML;

    // Re-initialize product elements after grid update (for sort/filter)
    if (typeof window.initializeProductElements === 'function') {
      window.initializeProductElements(target);
    }
  }

  static renderProductCount(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    if (!source || !container) return;

    container.innerHTML = source.innerHTML;
    container.classList.remove('loading');
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    if (!facetDetailsElements.length) return;

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`#FacetFiltersForm .js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const currentFilter = event?.target?.closest('.js-filter');
      if (currentFilter) FacetFiltersForm.renderCounts(countsToRender, currentFilter);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetsElement = html.querySelector('.active-facets');
    const target = document.querySelector('.active-facets');
    if (activeFacetsElement && target) {
      FacetFiltersForm.animateActiveFacets(target, activeFacetsElement.innerHTML);
    }

    FacetFiltersForm.toggleActiveFacets(false);
  }

  // Swap the chips row without jumps: removed chips fade out, remaining chips slide
  // to their new spots, new chips fade in, and the row eases to its new height.
  static animateActiveFacets(target, newHTML) {
    const chipsOf = (el) => Array.from(el.querySelectorAll(':scope > facet-remove'));
    const keyOf = (chip) =>
      chip.classList.contains('active-facets__button-wrapper')
        ? '__clear-all'
        : (chip.querySelector('.active-facets__button-inner > span:not(.visually-hidden)') || chip).textContent.trim();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      target.innerHTML = newHTML;
      return;
    }

    clearTimeout(target.chipSwapTimer);
    clearTimeout(target.chipSettleTimer);

    const incoming = document.createElement('div');
    incoming.innerHTML = newHTML;
    const newKeys = new Set(chipsOf(incoming).map(keyOf));
    const oldChips = chipsOf(target);
    const oldKeys = new Set(oldChips.map(keyOf));
    const leaving = oldChips.filter((chip) => !newKeys.has(keyOf(chip)));
    const startHeight = target.offsetHeight;

    target.classList.add('is-animating');
    target.style.height = `${startHeight}px`;
    leaving.forEach((chip) => chip.classList.add('is-leaving'));

    const swap = () => {
      const before = new Map(chipsOf(target).map((chip) => [keyOf(chip), chip.getBoundingClientRect()]));
      target.innerHTML = newHTML;
      const chips = chipsOf(target);
      const hasChips = chips.some((chip) => keyOf(chip) !== '__clear-all');

      target.style.height = 'auto';
      const endHeight = hasChips ? target.offsetHeight : 0;
      target.style.height = `${startHeight}px`;

      chips.forEach((chip) => {
        const key = keyOf(chip);
        const prev = oldKeys.has(key) ? before.get(key) : null;
        if (!prev) {
          chip.classList.add('is-entering');
          return;
        }
        const next = chip.getBoundingClientRect();
        const dx = prev.left - next.left;
        const dy = prev.top - next.top;
        if (!dx && !dy) return;
        chip.style.transition = 'none';
        chip.style.transform = `translate(${dx}px, ${dy}px)`;
      });

      target.offsetHeight; // commit the starting positions before animating
      target.style.height = `${endHeight}px`;
      chips.forEach((chip) => {
        chip.style.transition = '';
        chip.style.transform = '';
      });

      target.chipSettleTimer = setTimeout(() => {
        target.classList.remove('is-animating');
        target.style.height = '';
        chipsOf(target).forEach((chip) => chip.classList.remove('is-entering'));
      }, 320);
    };

    if (leaving.length) {
      target.chipSwapTimer = setTimeout(swap, 180);
    } else {
      swap();
    }
  }

  static renderAdditionalElements(html) {
    setTimeout(() => {
      const promoBanners = document.querySelectorAll('.promo-banner');
      if (promoBanners[0] != null) {
        promoBanners.forEach((el) => {
          el.classList.add('scroll-animation--active');
        });
      }
    }, 1000);

    // Re-bind accordion toggles after filter markup is replaced via AJAX
    document.querySelectorAll('facet-filters-form [id^="Details-"]').forEach((el) => {
      const summary = el.querySelector('.summary');
      if (summary && el.classList.contains('details--active')) {
        summary.setAttribute('aria-expanded', 'true');
      }
      new Accordion(el);
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
      if (typeof Accordion === 'function') new Accordion(target);
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const sections = [];
    const productGrid = document.getElementById('product-grid');
    if (productGrid?.dataset.id) {
      sections.push({ section: productGrid.dataset.id });
    }

    // Facets live in the collection banner/toolbar section, not the product grid.
    const facetsToolbar = document.querySelector('.toolbar[data-id], aside.toolbar[data-id]');
    if (facetsToolbar?.dataset.id && facetsToolbar.dataset.id !== productGrid?.dataset.id) {
      sections.push({ section: facetsToolbar.dataset.id });
    }

    return sections;
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];

      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('.field__input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
    this.querySelectorAll('.price-range__slider input').forEach((element) =>
      element.addEventListener('change', this.setSliderValues.bind(this))
    );
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('.field__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    const ranges = this.querySelectorAll('.price-range__slider input');

    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));

    ranges[0].value = minInput.value;
    ranges[1].value = maxInput.value;
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  setSliderValues() {
    const ranges = this.querySelectorAll('.price-range__slider input');
    let range1 = Number(ranges[0].value);
    let range2 = Number(ranges[1].value);
    const inputs = this.querySelectorAll('.field__input');

    if (range1 > range2) {
      let tmp = range2;
      range2 = range1;
      range1 = tmp;
    }

    inputs[0].value = range1;
    inputs[1].value = range2;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    const modalBtn = this.querySelector('.filter-modal__close');

    if (modalBtn) {
      modalBtn.addEventListener('click', (el) => {
        el.target.closest('details').querySelector('summary').click();
      });
    }

    if (!facetLink) return;

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'SPACE' && event.code.toUpperCase() !== 'ENTER') return;
      event.preventDefault();
      this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    // Start fading the chip(s) right away instead of waiting for the new results
    const row = this.closest('.active-facets');
    if (row && !this.classList.contains('active-facets__button-wrapper')) {
      this.classList.add('is-leaving');
    } else {
      document.querySelectorAll('.active-facets > facet-remove:not(.active-facets__button-wrapper)').forEach((chip) => chip.classList.add('is-leaving'));
    }
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (!form) return;
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
